import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useState } from 'react';
import { useSetAtom } from 'jotai';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderButton } from '@/ui/components/header';
import {
  Sidenav,
  SidenavButton,
  SidenavEmpty,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavList,
} from '@/ui/components/sidenav';
import { Icon } from '@/ui/components/icon';
import {
  FileBookmarkIcon,
  MoreHorizontalIcon,
  PencilEdit02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  extractionListTemplateQuery,
  extractionTemplateQuery,
} from '@/features/extractions/queries';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/ui/components/spinner';
import { TemplateActionsPopup } from '@/features/extractions/components/template-actions-popup';
import { MainScrollArea } from '@/ui/components/main';
import { Menu, MenuTrigger } from '@/ui/components/menu';
import { Button } from '@/ui/components/button';
import { EditExtraction } from '@/features/extractions/components/edit-extraction';
import { ExtractionTemplateDetails } from '@/features/extractions/components/extraction-template-details';
import { editOrganizationExtractionOpenAtom } from '@/features/extractions/store';

export const Route = createFileRoute('/_authed/settings/templates/$templateId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { templateId } = Route.useParams();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | null>(null);
  const setEditDialogOpen = useSetAtom(editOrganizationExtractionOpenAtom);

  const { data: templateData, isLoading: isLoadingTemplate } = useQuery(
    extractionListTemplateQuery(organizationId, templateId),
  );

  const activeExtractionId =
    selectedExtractionId ?? templateData?.templates?.[0]?.customTopicTemplateId;
  const { data: extractionDetails, isLoading: isLoadingExtraction } = useQuery({
    ...extractionTemplateQuery(organizationId, activeExtractionId ?? ''),
    enabled: !!activeExtractionId,
  });

  return (
    <>
      <Header>
        <HeaderBackButton />
        <Breadcrumb>
          <BreadcrumbLink to={'/settings/templates'}>
            <Icon icon={FileBookmarkIcon} />
            {t({ en: 'Templates', nb: 'Maler' })}
          </BreadcrumbLink>
          {templateData && <BreadcrumbPage>{templateData.name}</BreadcrumbPage>}
        </Breadcrumb>
        {templateData && (
          <Menu>
            <MenuTrigger
              render={
                <HeaderButton aria-label="More options">
                  <Icon icon={MoreHorizontalIcon} />
                </HeaderButton>
              }
            />
            <TemplateActionsPopup template={templateData} align="start" />
          </Menu>
        )}
      </Header>
      <MainScrollArea viewportClassName="@3xl:flex-row flex flex-col grow">
        <Sidenav className="@3xl:border-r">
          <SidenavHeader>
            <SidenavHeading>
              {t({ en: 'Extractions:', nb: 'Uttrekk:' })}{' '}
              <strong className="text-foreground">{templateData?.templates?.length ?? 0}</strong>
            </SidenavHeading>
          </SidenavHeader>
          <SidenavList>
            {isLoadingTemplate ? (
              <Spinner kind="inset" />
            ) : !templateData?.templates || templateData.templates.length === 0 ? (
              <SidenavEmpty>{t({ en: 'No extractions', nb: 'Ingen uttrekk' })}</SidenavEmpty>
            ) : (
              templateData.templates.map((extraction, index) => (
                <SidenavItem key={extraction.customTopicTemplateId} className="w-full">
                  <SidenavButton
                    active={activeExtractionId === extraction.customTopicTemplateId}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedExtractionId(extraction.customTopicTemplateId);
                    }}
                  >
                    <span className="line-clamp-1 max-w-24">
                      <span className="w-5">{index + 1}.</span> {extraction.userQuery}
                    </span>
                  </SidenavButton>
                </SidenavItem>
              ))
            )}
          </SidenavList>
        </Sidenav>
        <div className="grow flex relative flex-col min-w-0 gap-4">
          {isLoadingExtraction ? (
            <Spinner kind="inset" />
          ) : extractionDetails ? (
            <div className="grow p-5 overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">{extractionDetails.userQuery}</h3>
                <Button variant="secondary" kind="icon" onClick={() => setEditDialogOpen(true)}>
                  <Icon icon={PencilEdit02Icon} />
                </Button>
              </div>
              <ExtractionTemplateDetails templateId={extractionDetails.id} />
            </div>
          ) : (
            <p className="text-muted absolute inset-0 m-auto">
              {t({
                en: 'Select an extraction to view details',
                nb: 'Velg et uttrekk for å se detaljer',
              })}
            </p>
          )}
        </div>
        {extractionDetails && <EditExtraction templateId={extractionDetails.id} />}
      </MainScrollArea>
    </>
  );
}
